/**
 * Every payment this programme has moved, and why.
 *
 * This is the operational answer to "when does she get it". A counsellor
 * cannot ring "payout 9102", so names are here, and so is the arithmetic that
 * produced the date — which period was full, by how much, and what the ceiling
 * was at the time.
 *
 * Ordered by the date the money arrives, soonest first, because that is the
 * order someone works the queue in.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarClock, CheckCircle2, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { formatDateOnly } from '../../utils/dateTime';
import { benefitsApi, type DeferralRow } from './types';

const money = (v: string) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(v));

interface Props {
  programId: number;
}

export const DeferralQueue: React.FC<Props> = ({ programId }) => {
  const [rows, setRows] = useState<DeferralRow[]>([]);
  const [openOnly, setOpenOnly] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await benefitsApi.listDeferrals(programId, openOnly);
      setRows(r.deferrals);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load deferrals.');
    } finally {
      setLoading(false);
    }
  }, [programId, openOnly]);

  useEffect(() => {
    void load();
  }, [load]);

  const total = useMemo(
    () => rows.reduce((sum, r) => sum + Number(r.amount), 0),
    [rows],
  );

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base">Scheduled payments</CardTitle>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant={openOnly ? 'default' : 'outline'}
              onClick={() => setOpenOnly(true)}
            >
              Still held
            </Button>
            <Button
              size="sm"
              variant={openOnly ? 'outline' : 'default'}
              onClick={() => setOpenOnly(false)}
            >
              All
            </Button>
            <Button size="sm" variant="ghost" onClick={() => void load()} disabled={loading}>
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <RefreshCw className="h-4 w-4" aria-hidden="true" />
              )}
            </Button>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          Payments moved out of a period that was already full. The money is not lost — it has a
          date.
        </p>
      </CardHeader>

      <CardContent>
        {error && <p className="mb-3 text-sm text-destructive">{error}</p>}

        {rows.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {loading ? 'Loading…' : 'Nothing is being held.'}
          </p>
        ) : (
          <>
            <p className="mb-3 text-sm tabular-nums text-muted-foreground">
              {rows.length} payment
              {rows.length === 1 ? '' : 's'} · {money(String(total))}
            </p>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[42rem] text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="py-2 pr-3 font-medium">Member</th>
                    <th className="py-2 pr-3 font-medium">Arrives</th>
                    <th className="py-2 pr-3 text-right font-medium">Amount</th>
                    <th className="py-2 pr-3 font-medium">Period that was full</th>
                    <th className="py-2 text-right font-medium">Would have reached</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-b border-border/60 last:border-0">
                      <td className="py-2.5 pr-3">
                        <span className="font-medium text-foreground">
                          {r.member_name?.trim() || `Member ${r.member_id}`}
                        </span>
                        <span className="ml-2 text-xs text-muted-foreground">#{r.payout_id}</span>
                      </td>
                      <td className="py-2.5 pr-3 tabular-nums">
                        <span className="inline-flex items-center gap-1.5">
                          {r.released_at ? (
                            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" aria-hidden="true" />
                          ) : (
                            <CalendarClock className="h-3.5 w-3.5 text-sky-600" aria-hidden="true" />
                          )}
                          {formatDateOnly(r.deferred_to)}
                        </span>
                      </td>
                      <td className="py-2.5 pr-3 text-right tabular-nums">{money(r.amount)}</td>
                      <td className="py-2.5 pr-3 text-xs tabular-nums text-muted-foreground">
                        {formatDateOnly(r.period_start)} – {formatDateOnly(r.period_end)} ·{' '}
                        {money(r.period_total_before)} of {money(r.period_cap)}
                      </td>
                      <td className="py-2.5 text-right tabular-nums text-orange-700 dark:text-orange-300">
                        {money(r.would_have_been)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default DeferralQueue;
